/**
 * The board's two resting faces — the 22:00 **invite state** and the 01:00
 * **night** state (product ticket 04, CONTEXT.md "Invite state").
 *
 * Why this one lives outside `renderBoard`: the board re-renders by replacing
 * `#root.innerHTML`, which restarts every CSS transition it contains. A face
 * that is supposed to arrive *gently* cannot be re-mounted every 20 seconds,
 * so the arc owns a stable overlay element and only touches the parts that
 * actually changed — the panels are rebuilt when the Directions change, the
 * clock is a text node.
 *
 * Imagery is the real content here: full-bleed Direction images, gradient
 * placeholders until `defs/images/` has bytes (same source of truth as the
 * strip — `directionImage`).
 */

import type { ArcState } from '../day-arc';
import type { CalendarEvent, DefsDirection } from '../types';
import { directionImage, esc, hhmm } from './util';

export interface ArcViewData {
  directions: DefsDirection[];
  /** the one whisper of tomorrow — the next calendar event, or null. */
  next: CalendarEvent | null;
  now: Date;
}

export interface ArcMount {
  /** `data` may be null before the first poll lands. */
  update(state: ArcState, data: ArcViewData | null): void;
  el: HTMLElement;
}

export function mountArc(host: HTMLElement = document.body): ArcMount {
  const el = document.createElement('div');
  el.className = 'arc';
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = `<div class="arc-panels"></div>
    <div class="arc-foot">
      <div class="arc-clock num"></div>
      <div class="arc-whisper"></div>
    </div>`;
  host.appendChild(el);

  const panels = el.querySelector('.arc-panels') as HTMLElement;
  const clock = el.querySelector('.arc-clock') as HTMLElement;
  const whisper = el.querySelector('.arc-whisper') as HTMLElement;

  let panelSig = '';

  return {
    el,
    update(state, data) {
      const showing = state.face !== 'board';
      el.classList.toggle('show', showing);
      el.classList.toggle('night', state.face === 'night');
      el.setAttribute('aria-hidden', showing ? 'false' : 'true');

      // Nothing below is visible while the board is up; skip the churn so a
      // kiosk sitting in the normal state does no work for the arc at all.
      if (!showing || !data) return;

      const sig = panelSignature(data.directions);
      if (sig !== panelSig) {
        panelSig = sig;
        panels.innerHTML = renderPanels(data.directions);
      }

      const hm = data.now.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      if (clock.textContent !== hm) clock.textContent = hm;

      // The night face is a clock on near-black and nothing else — no whisper,
      // no imagery to read. Display sleep takes it from there.
      const text = state.face === 'night' ? '' : whisperText(data.next, data.now);
      if (whisper.textContent !== text) whisper.textContent = text;
    },
  };
}

function panelSignature(directions: DefsDirection[]): string {
  return directions.map((d) => `${d.id}|${d.image ?? ''}|${d.heading}`).join('~');
}

/**
 * The invite verb given the whole screen: one full-height panel per Direction,
 * imagery first, the heading and its why quiet underneath. No task machinery,
 * no counts, nothing to do — that is the point.
 */
function renderPanels(directions: DefsDirection[]): string {
  return directions
    .map(
      (d, i) => `<div class="arc-panel">
        <div class="arc-img" style="background-image:${directionImage(d)};animation-delay:${
          i * -9
        }s"></div>
        <div class="arc-veil"></div>
        <div class="arc-cap">
          <span class="h">${esc(d.heading)}</span>
          <span class="w">${esc(d.why)}</span>
        </div>
      </div>`,
    )
    .join('');
}

/**
 * "optionally one whisper of tomorrow (first calendar event)" — product
 * ticket 04. Rendered only when there actually is a next event; the invite
 * state stays wordless otherwise.
 */
export function whisperText(next: CalendarEvent | null, now: Date): string {
  if (!next) return '';
  const start = new Date(next.start);
  if (Number.isNaN(start.getTime())) return '';
  const lead = dayLead(start, now);
  return next.allDay ? `${lead} · ${next.title}` : `${lead} · ${hhmm(next.start)} ${next.title}`;
}

/** "later" / "tomorrow" / a weekday — the invite state never counts days. */
function dayLead(start: Date, now: Date): string {
  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((midnight(start) - midnight(now)) / 86_400_000);
  if (days <= 0) return 'later';
  if (days === 1) return 'tomorrow';
  return start.toLocaleDateString([], { weekday: 'long' }).toLowerCase();
}
